/* ============================================================================
 * HADAT — Hair Ritual Quiz engine
 * ----------------------------------------------------------------------------
 * Drives the multi-screen quiz rendered by sections/quiz.liquid:
 *   q1..q6  → single-select questions (4 phases: TYPE · CONCERN · RITUAL · RESULT)
 *   email   → name + email gate (required to unlock the result)
 *   result  → "{Name}, here is your personalized HADAT ritual." hero + order-of-use
 *             narrative + one-line ritual strip (cards tagged Cleanse · Treat ·
 *             Style · Body/Hands) + buy-all + "Add to your bundle" upsells
 *
 * The product-mapping logic is deterministic and follows
 * docs/hadat-quiz-specification.md §05 (product mapping) + §08 (edge cases).
 * Product → Shopify product wiring is editor-managed: the section renders a
 * JSON library of every ritual product keyed by `product_key`; this engine
 * only decides which keys to show.
 * ========================================================================== */
(function () {
  'use strict';

  var ANIM_MS = 440;
  var reducedMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  /* ── Ritual names are injected from Liquid via data attributes (initQuiz). ── */

  var STEP_ORDER = ['cleanse', 'treat', 'style', 'body_hands'];

  /* "Add to your bundle" upsell candidates. MASK_OIL_KEYS decides whether a
   * ritual already carries an indulgence step; UPSELL_FILL is the fixed
   * fallback priority once the answer-driven candidates run out - order
   * matters, it keeps the pick deterministic. */
  var MASK_OIL_KEYS = ['spa-treatment', 'golden-mask', 'microcapsules-mask', 'silk-oil'];
  var UPSELL_FILL = [
    'pearl-serum', 'silk-oil', 'molecular-cream', 'salt-spray', 'golden-mask',
    'microcapsules-mask', 'spa-treatment', 'silk-treatment', 'mud-scrub',
    'balmy-hand-cream', 'calm-body-cream', 'calm-hand-cream'
  ];

  /* Inline stylesheet for the standalone "Save as PDF" sheet (printed from a
   * hidden iframe, see saveRitualPdf); self-contained on purpose: printing
   * the shop page itself drags in site chrome, app embeds and theme-CSS
   * loading quirks, while a dedicated document always prints clean. */
  var PRINT_SHEET_CSS =
    /* Zero page margins: browsers draw their date/URL headers and footers in
     * the margin band, so none leaves them nothing to print on. The sheet
     * pads itself via body padding instead. */
    '@page { size: A4; margin: 0; }' +
    '* { box-sizing: border-box; margin: 0; padding: 0; }' +
    "body { font-family: 'Montserrat', 'Helvetica Neue', Arial, sans-serif; color: #1d1d1b; padding: 14mm 16mm; }" +
    '.head { text-align: center; padding-bottom: 20px; margin-bottom: 26px; border-bottom: 1px solid #d8d3cb; }' +
    ".brand { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 22px; letter-spacing: 0.42em; text-indent: 0.42em; text-transform: uppercase; margin-bottom: 14px; }" +
    '.brand span { display: block; margin-top: 3px; font-family: inherit; font-size: 9px; letter-spacing: 0.55em; text-indent: 0.55em; color: #6b6b6b; }' +
    '.for { font-size: 10px; letter-spacing: 0.18em; text-indent: 0.18em; text-transform: uppercase; color: #6b6b6b; margin-bottom: 8px; }' +
    "h1 { font-family: 'Cormorant Garamond', Georgia, serif; font-weight: 400; font-size: 32px; line-height: 1.05; margin-bottom: 10px; }" +
    '.url { font-size: 8.5px; color: #8a8a8a; word-break: break-all; }' +
    '.step { margin-bottom: 22px; break-inside: avoid; }' +
    '.step header { display: flex; justify-content: space-between; align-items: baseline; padding-bottom: 6px; margin-bottom: 12px; border-bottom: 1px solid #e5e1da; }' +
    '.step h2 { font-size: 12px; font-weight: 400; letter-spacing: 0.14em; text-transform: uppercase; }' +
    '.step header span { font-size: 10px; color: #8a8a8a; }' +
    '.grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }' +
    '.card { break-inside: avoid; }' +
    '.card img, .card-ph { display: block; width: 100%; aspect-ratio: 4 / 5; object-fit: cover; background: #f4f1ec; margin-bottom: 8px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }' +
    '.card h3 { font-size: 9.5px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; margin-bottom: 2px; }' +
    '.price { font-size: 9.5px; color: #444444; margin-bottom: 4px; }' +
    ".why { font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic; font-size: 11px; line-height: 1.4; color: #6b6b6b; }" +
    "footer { margin-top: 26px; padding-top: 14px; border-top: 1px solid #d8d3cb; text-align: center; font-family: 'Cormorant Garamond', Georgia, serif; font-size: 15px; }";

  var PRINT_SHEET_FONTS =
    '<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;1,400&display=swap">' +
    '<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;600&display=swap">';

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* Same-browser contact persistence. The shareable URL deliberately carries
   * no email (links travel: PII in URLs leaks via referrers, analytics and
   * forwarded messages), so the gate contact lives in localStorage instead.
   * A reload or a hand-edited ?name= keeps the email-save button working for
   * the quiz taker; a stranger opening a shared link has no stored contact
   * and the button stays hidden. Cleared on retake. */
  var CONTACT_KEY = 'hadat:quiz:contact';
  function readStoredContact() {
    try {
      return JSON.parse(window.localStorage.getItem(CONTACT_KEY) || 'null') || {};
    } catch (e) {
      return {};
    }
  }
  function writeStoredContact(contact) {
    try { window.localStorage.setItem(CONTACT_KEY, JSON.stringify(contact)); } catch (e) {}
  }
  function clearStoredContact() {
    try { window.localStorage.removeItem(CONTACT_KEY); } catch (e) {}
  }

  /* ─────────────────────────────────────────────────────────────────────
   * Money formatting (classic Shopify helper) — formats cents using the
   * shop's money_format string (e.g. "{{amount}} €", "€{{amount_no_decimals}}").
   * ───────────────────────────────────────────────────────────────────── */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    format = format || '${{amount}}';

    function defaultTo(num, precision, thousands, decimal) {
      precision = isNaN(precision) ? 2 : precision;
      thousands = thousands === undefined ? ',' : thousands;
      decimal = decimal === undefined ? '.' : decimal;
      if (isNaN(num) || num == null) return 0;
      num = (num / 100.0).toFixed(precision);
      var parts = num.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var cents = parts[1] ? decimal + parts[1] : '';
      return dollars + cents;
    }

    switch ((format.match(placeholderRegex) || [])[1]) {
      case 'amount': value = defaultTo(cents, 2); break;
      case 'amount_no_decimals': value = defaultTo(cents, 0); break;
      case 'amount_with_comma_separator': value = defaultTo(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = defaultTo(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = defaultTo(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = defaultTo(cents, 0, ' ', ''); break;
      case 'amount_with_apostrophe_separator': value = defaultTo(cents, 2, "'", '.'); break;
      default: value = defaultTo(cents, 2);
    }
    return format.replace(placeholderRegex, value);
  }

  /* ── Analytics — push to dataLayer + dispatch a DOM event. Respects an
   * optional cookie-consent gate (Shopify Customer Privacy API). ── */
  function consentGiven() {
    try {
      if (window.Shopify && window.Shopify.customerPrivacy &&
          typeof window.Shopify.customerPrivacy.analyticsProcessingAllowed === 'function') {
        return window.Shopify.customerPrivacy.analyticsProcessingAllowed();
      }
    } catch (e) {}
    return true; // No CMP detected → don't block the funnel.
  }

  /* Locale strings occasionally arrive HTML-escaped from the theme content DB
   * (e.g. "The Volume &amp; Lift Ritual"), which a textContent write would show
   * literally. Decode through a detached <textarea> (inert, no script/element
   * execution) so ritual names always render the real characters. */
  var entityDecoder = document.createElement('textarea');
  function decodeEntities(s) {
    if (!s || s.indexOf('&') === -1) return s;
    entityDecoder.innerHTML = s;
    return entityDecoder.value;
  }

  function track(event, detail) {
    detail = detail || {};
    if (consentGiven()) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: event }, detail));
    }
    // Bridge funnel events to the GTM Custom Pixel (its sandbox dataLayer is
    // isolated from this page). Delivery is gated by the pixel's Required
    // consent setting, so we publish unconditionally here.
    if (window.Shopify && window.Shopify.analytics &&
        typeof window.Shopify.analytics.publish === 'function') {
      window.Shopify.analytics.publish('hadat:quiz', Object.assign({ quiz_event: event }, detail));
    }
    document.dispatchEvent(new CustomEvent('quiz:' + event, { detail: detail }));
  }

  /* ─────────────────────────────────────────────────────────────────────
   * DETERMINISTIC RITUAL BUILDER  (spec §05 + §08)
   * Input: answers = { q1: hair_*, q2: scalp_*, q3: goal_*, q4: life_*,
   *                    q5: ritual_*, q6: add_* }
   * Output: { cleanse:[keys], treat:[keys], style:[keys], body_hands:[keys] }
   * ───────────────────────────────────────────────────────────────────── */
  function pickFirst(rules, fallback) {
    for (var i = 0; i < rules.length; i++) {
      if (rules[i][0]) return rules[i][1];
    }
    return fallback;
  }

  function buildRitual(a) {
    var steps = { cleanse: [], treat: [], style: [], body_hands: [] };
    var heat = a.q4 === 'life_heat';
    var exposed = ['life_heat', 'life_sun', 'life_city', 'life_sport'].indexOf(a.q4) !== -1;
    var lifeMinimal = a.q4 === 'life_minimal';
    var minimal = a.q5 === 'ritual_minimal' || a.q5 === 'ritual_fast';
    var sensorial = a.q5 === 'ritual_sensorial';
    var layered = a.q5 === 'ritual_layered';
    var thickish = a.q1 === 'hair_thick' || a.q1 === 'hair_curly';

    /* STEP 1 - CLEANSE (one shampoo, first matching trigger). Oily/growth pairs
     * the Root Strengthening Shampoo with a weekly pre-cleanse scrub (spec §05
     * row 1; Mud Shampoo is not in the catalog - client-approved swap). The
     * shampoo pick stays in a var: the trim rules below must always keep the
     * shampoo and only ever drop the scrub, which sits at index 0 by invariant. */
    var oilyOrGrowth = a.q2 === 'scalp_oily' || a.q3 === 'goal_growth';
    var shampoo = pickFirst([
      [oilyOrGrowth || a.q2 === 'scalp_thinning', 'root-shampoo'],
      [a.q2 === 'scalp_dry' || a.q3 === 'goal_hydration', 'moisture-shampoo'],
      [a.q3 === 'goal_repair' || heat, 'repair-shampoo'],
      [a.q3 === 'goal_shine' || a.q1 === 'hair_fine', 'silk-shampoo'],
      [a.q2 === 'scalp_sensitive', 'microcapsules-shampoo'],
      [a.q3 === 'goal_color', 'golden-shampoo']
    ], 'moisture-shampoo');
    steps.cleanse.push(shampoo);
    if (oilyOrGrowth) addFirst(steps.cleanse, 'mud-scrub');

    /* STEP 2 — TREAT (one main conditioner/treatment, first match else default) */
    var treat = pickFirst([
      [a.q3 === 'goal_shine' && (sensorial || layered), 'silk-conditioner'],
      [a.q3 === 'goal_repair' && heat, 'deep-repair-treatment'],
      [a.q3 === 'goal_hydration' && thickish, 'spa-treatment'],
      [a.q3 === 'goal_repair' && a.q1 === 'hair_fine', 'silk-treatment'],
      [a.q3 === 'goal_color' && sensorial, 'golden-mask'],
      [a.q2 === 'scalp_sensitive' && sensorial, 'microcapsules-mask'],
      [a.q3 === 'goal_repair' && layered, 'deep-repair-treatment']
    ], 'nourishing-conditioner');
    steps.treat.push(treat);

    /* STEP 3 — STYLE (one styling product; Hero serum is the default) */
    steps.style.push(pickFirst([
      [a.q3 === 'goal_volume', 'salt-spray'],
      [a.q3 === 'goal_shine' && minimal, 'silk-oil'],
      [a.q3 === 'goal_shine' && a.q1 === 'hair_fine', 'pearl-serum'],
      [minimal && (a.q1 === 'hair_medium' || a.q1 === 'hair_thick'), 'molecular-cream'],
      [exposed, 'miracle-serum']
    ], 'miracle-serum'));

    /* ── Ritual-size additions (spec: sensorial=4, layered=5) ── */
    if (sensorial) {
      // adds a mask or oil
      if (a.q3 === 'goal_shine') add(steps.treat, 'silk-oil');
      else add(steps.style, 'silk-oil');
    }
    if (layered) {
      // adds scrub (pre-shampoo, index 0; no-op when already added above) + an extra treatment
      if (a.q2 === 'scalp_oily' || a.q2 === 'scalp_thinning') addFirst(steps.cleanse, 'mud-scrub');
      if (a.q3 === 'goal_shine') {
        add(steps.treat, 'silk-oil');
      } else {
        // The extra treatment must be DISTINCT from the main one, otherwise the
        // de-dupe below (and the render-time variant de-dupe) swallows it and the
        // "layered" ritual collapses toward 3 products. Fall back to a mask that
        // isn't already present.
        add(steps.treat, steps.treat.indexOf('spa-treatment') === -1 ? 'spa-treatment' : 'golden-mask');
      }
    }

    /* ── Edge cases (spec §08) ── */
    // scalp_oily + goal_hydration → shampoo + Nutrient Conditioner + Hydro Spa
    // (lengths). §08 prescribes that three-part core, so the scrub gives way -
    // otherwise the pair would blow the sensorial target and drop the Spa instead.
    if (a.q2 === 'scalp_oily' && a.q3 === 'goal_hydration') {
      steps.cleanse = [shampoo];
      steps.treat = ['nourishing-conditioner'];
      add(steps.treat, 'spa-treatment');
    }
    // goal_volume + thick → keep Salt Spray, swap Liquid Silk Oil → Restoring Pearl Serum
    if (a.q3 === 'goal_volume' && a.q1 === 'hair_thick') {
      swap(steps.style, 'silk-oil', 'pearl-serum');
      swap(steps.treat, 'silk-oil', 'pearl-serum');
    }

    /* STEP 4 — BODY & HANDS (from Q6) */
    if (a.q6 === 'add_body' || a.q6 === 'add_both') {
      steps.body_hands.push('calm-shower-gel');
      steps.body_hands.push(a.q2 === 'scalp_sensitive' && a.q6 === 'add_body'
        ? 'calm-body-lotion' : 'calm-body-cream');
    }
    if (a.q6 === 'add_hands' || a.q6 === 'add_both') {
      add(steps.body_hands, pickFirst([
        [a.q2 === 'scalp_sensitive', 'calm-hand-cream'],
        [sensorial, 'balmy-hand-cream'],
        [minimal, 'aya-hand-cream']
      ], 'aya-hand-cream'));
    }

    /* ── Size enforcement ── */
    // minimal/fast (Q5) OR a low-exposure lifestyle (Q4 life_minimal) → keep
    // the hair ritual to one product per hair step (spec §03 Q4: "minimal"
    // caps the ritual at 3 products instead of 5).
    if (minimal || lifeMinimal) {
      steps.cleanse = [shampoo]; // target 3: the scrub is the first cut, never the shampoo
      steps.treat = steps.treat.slice(0, 1);
      steps.style = steps.style.slice(0, 1);
    }
    // minimal + add_both → hair ritual stays at 3 (already enforced above).

    // Final size cap by ritual type (spec §05: sensorial=4, layered=5). An edge
    // case can stack a treatment on top of a size bonus (e.g. scalp_oily +
    // goal_hydration + sensorial → 5), so trim any overflow above the target.
    // Pop generic extras from style, then treat, then the pre-cleanse scrub
    // (always index 0 of cleanse) as the last resort - the client-approved
    // scrub+shampoo cleanse deliberately outranks the soft sensorial "mask or
    // oil" bonus, so at target 4 the style/treat extra goes first. The shampoo
    // and at least one product per hair step always survive.
    var targetHair = (minimal || lifeMinimal) ? 3 : (sensorial ? 4 : (layered ? 5 : 3));
    var hairCount = function () {
      return steps.cleanse.length + steps.treat.length + steps.style.length;
    };
    while (hairCount() > targetHair) {
      if (steps.style.length > 1) steps.style.pop();
      else if (steps.treat.length > 1) steps.treat.pop();
      else if (steps.cleanse.length > 1) steps.cleanse.shift();
      else break;
    }

    // De-dupe across the whole ritual (a product belongs to a single step).
    var seen = {};
    STEP_ORDER.forEach(function (step) {
      steps[step] = steps[step].filter(function (k) {
        if (!k || seen[k]) return false;
        seen[k] = true;
        return true;
      });
    });
    return steps;
  }

  function add(arr, key) { if (key && arr.indexOf(key) === -1) arr.push(key); }
  function addFirst(arr, key) { if (key && arr.indexOf(key) === -1) arr.unshift(key); }
  function swap(arr, from, to) {
    var i = arr.indexOf(from);
    if (i !== -1) arr[i] = to;
  }

  /* Email/ad click-through junk stripped from the shareable URL: the ritual
   * email arrives with Shopify Email tracking params attached, and a copied
   * link should stay clean. */
  var TRACKING_PARAMS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
    'syclid', 'se_activity_id', 'fbclid', 'gclid', 'ttclid'
  ];
  function stripTrackingParams(url) {
    TRACKING_PARAMS.forEach(function (p) { url.searchParams.delete(p); });
  }

  /* ── Compact answer encoding for shareable result URLs (?ritual=...) ── */
  var Q_KEYS = ['q1', 'q2', 'q3', 'q4', 'q5', 'q6'];

  /* Whitelist of valid option IDs per question. Shared/deep-link URLs are
   * untrusted input — anything outside this set is dropped so a tampered or
   * malformed link can never build a broken ritual. */
  var VALID_OPTIONS = {
    q1: ['hair_fine', 'hair_medium', 'hair_thick', 'hair_wavy', 'hair_curly'],
    q2: ['scalp_oily', 'scalp_dry', 'scalp_balanced', 'scalp_sensitive', 'scalp_thinning'],
    q3: ['goal_shine', 'goal_repair', 'goal_hydration', 'goal_volume', 'goal_color', 'goal_growth'],
    q4: ['life_heat', 'life_sun', 'life_city', 'life_sport', 'life_minimal'],
    q5: ['ritual_fast', 'ritual_sensorial', 'ritual_layered', 'ritual_minimal'],
    q6: ['add_none', 'add_body', 'add_hands', 'add_both']
  };

  function safeDecode(s) {
    try { return decodeURIComponent(s); } catch (e) { return ''; }
  }

  function encodeAnswers(answers) {
    return Q_KEYS.map(function (q) { return encodeURIComponent(answers[q] || ''); }).join('~');
  }
  function decodeAnswers(str) {
    var parts = String(str).split('~');
    var out = {};
    Q_KEYS.forEach(function (q, i) {
      var v = parts[i] ? safeDecode(parts[i]) : '';
      if (v && VALID_OPTIONS[q].indexOf(v) !== -1) out[q] = v;
    });
    return out;
  }

  /* ═══════════════════════════════════════════════════════════════════════
   * QUIZ CONTROLLER
   * ═══════════════════════════════════════════════════════════════════════ */
  function initQuiz(root) {
    var moneyFormat = root.getAttribute('data-money-format') || '${{amount}}';
    var discountPercent = parseFloat(root.getAttribute('data-discount-percent')) || 0;
    var discountCode = (root.getAttribute('data-discount-code') || '').trim();
    var ritualEmailTag = (root.getAttribute('data-ritual-email-tag') || '').trim();
    var cartError = root.getAttribute('data-cart-error') || '';
    var soldOutLabel = root.getAttribute('data-sold-out') || 'Sold out';
    var ritualNames = {};
    var defaultRitualName = decodeEntities(root.getAttribute('data-ritual-name-default') || 'Your HADAT Ritual');
    var printRitualWithName = root.getAttribute('data-print-ritual-with-name') || '{{ name }} - HADAT ritual';
    var printRitualDefault = root.getAttribute('data-print-ritual-default') || 'Your HADAT ritual';
    try {
      ritualNames = JSON.parse(root.getAttribute('data-ritual-names') || '{}');
      Object.keys(ritualNames).forEach(function (k) {
        ritualNames[k] = decodeEntities(ritualNames[k]);
      });
    } catch (e) {
      ritualNames = {};
    }

    /* Product library: key → product data */
    var library = {};
    try {
      var raw = root.querySelector('[data-quiz-products]');
      (raw ? JSON.parse(raw.textContent) : []).forEach(function (p) { library[p.key] = p; });
    } catch (e) {}

    var screens = Array.prototype.slice.call(root.querySelectorAll('[data-quiz-screen]'));
    var questionScreens = screens.filter(function (s) {
      return /^q[1-6]$/.test(s.getAttribute('data-screen'));
    });
    var byName = {};
    screens.forEach(function (s) { byName[s.getAttribute('data-screen')] = s; });

    var answers = {};
    var current = null;
    var started = false;

    /* The global header stays opaque (white bg, dark content) on every quiz
     * screen, including the hero — its elements would otherwise blend into the
     * light hero background. Rendered opaque server-side; kept opaque here. */
    function applyHeaderMode(name) {
      var header = document.querySelector('.header');
      if (header) header.classList.add('header--default-dark');
    }

    /* ── Screen navigation ── */
    function clearTransitions() {
      screens.forEach(function (s) {
        if (s.classList.contains('is-leaving')) {
          s.style.top = s.style.left = s.style.width = s.style.height = '';
          s.classList.remove('is-leaving');
        }
        s.classList.remove('is-entering');
      });
    }

    function show(name, dir) {
      var outgoing = current ? byName[current] : null;
      var incoming = byName[name];
      if (!incoming || name === current) return;

      clearTransitions();

      var animate = !!dir && !reducedMotion;

      if (animate && outgoing) {
        var rect = outgoing.getBoundingClientRect();
        outgoing.style.top    = rect.top + 'px';
        outgoing.style.left   = rect.left + 'px';
        outgoing.style.width  = rect.width + 'px';
        outgoing.style.height = rect.height + 'px';
        outgoing.classList.add('is-leaving');

        var cleanLeave = function () {
          outgoing.style.top = outgoing.style.left = outgoing.style.width = outgoing.style.height = '';
          outgoing.classList.remove('is-leaving');
        };
        var tid = setTimeout(cleanLeave, 330);
        outgoing.addEventListener('animationend', function onEnd(e) {
          if (e.target !== outgoing) return;
          outgoing.removeEventListener('animationend', onEnd);
          clearTimeout(tid);
          cleanLeave();
        });
      }

      screens.forEach(function (s) { s.classList.remove('is-active'); });
      incoming.classList.add('is-active');

      if (animate) {
        incoming.classList.add('is-entering');
        setTimeout(function () { incoming.classList.remove('is-entering'); }, 330);
      }

      applyHeaderMode(name);
      current = name;
    }

    function indexOfQuestion(name) {
      for (var i = 0; i < questionScreens.length; i++) {
        if (questionScreens[i].getAttribute('data-screen') === name) return i;
      }
      return -1;
    }

    function goNextFrom(name) {
      if (!started) { started = true; track('quiz_started'); }
      var qi = indexOfQuestion(name);
      if (qi !== -1) {
        if (qi + 1 < questionScreens.length) {
          show(questionScreens[qi + 1].getAttribute('data-screen'), 'forward');
        } else {
          track('quiz_email_gate_viewed');
          show('email', 'forward');
        }
        return;
      }
      if (name === 'email') { submitEmail(); return; }
    }

    function goPrevFrom(name) {
      var qi = indexOfQuestion(name);
      if (qi > 0) { show(questionScreens[qi - 1].getAttribute('data-screen'), 'back'); return; }
      if (name === 'email') {
        show(questionScreens[questionScreens.length - 1].getAttribute('data-screen'), 'back');
      }
    }

    function startQuiz() {
      if (!started) { started = true; track('quiz_started'); }
      if (questionScreens[0]) show(questionScreens[0].getAttribute('data-screen'), 'hero');
    }

    /* ── Option selection ── */
    root.addEventListener('click', function (e) {
      var start = e.target.closest('[data-quiz-start]');
      if (start && root.contains(start)) {
        e.preventDefault();
        startQuiz();
        return;
      }

      var opt = e.target.closest('[data-quiz-option]');
      if (opt && root.contains(opt)) {
        var q = opt.getAttribute('data-question');
        var screen = byName[q];
        if (screen) {
          screen.querySelectorAll('[data-quiz-option]').forEach(function (o) {
            o.classList.remove('is-selected');
            o.setAttribute('aria-pressed', 'false');
          });
        }
        opt.classList.add('is-selected');
        opt.setAttribute('aria-pressed', 'true');
        answers[q] = opt.getAttribute('data-option-id');
        track('quiz_question_answered', { q_id: q, option_id: answers[q] });
        // Auto-advance: short delay lets the selection state render before transitioning.
        setTimeout(function() { goNextFrom(q); }, 300);
        return;
      }

      var why = e.target.closest('[data-quiz-why-toggle]');
      if (why && root.contains(why)) {
        var note = why.closest('[data-quiz-why]');
        if (note) {
          var open = note.classList.toggle('is-open');
          why.setAttribute('aria-expanded', open ? 'true' : 'false');
        }
        return;
      }

      var next = e.target.closest('[data-quiz-next]');
      if (next && root.contains(next)) {
        e.preventDefault();
        var scr = next.closest('[data-quiz-screen]');
        var name = scr ? scr.getAttribute('data-screen') : current;
        if (/^q[1-6]$/.test(name) && !answers[name]) return; // require a choice
        goNextFrom(name);
        return;
      }

      var prev = e.target.closest('[data-quiz-prev]');
      if (prev && root.contains(prev)) {
        e.preventDefault();
        var pscr = prev.closest('[data-quiz-screen]');
        goPrevFrom(pscr ? pscr.getAttribute('data-screen') : current);
        return;
      }

      var buyAll = e.target.closest('[data-quiz-buyall-btn]');
      if (buyAll && root.contains(buyAll)) {
        e.preventDefault();
        addFullRitual(buyAll);
        return;
      }

      var emailBtn = e.target.closest('[data-quiz-email-btn]');
      if (emailBtn && root.contains(emailBtn)) {
        e.preventDefault();
        sendRitualEmail(emailBtn);
        return;
      }

      var saveSubmit = e.target.closest('[data-quiz-save-submit]');
      if (saveSubmit && root.contains(saveSubmit)) {
        e.preventDefault();
        submitSaveForm(saveSubmit);
        return;
      }

      var changeBtn = e.target.closest('[data-quiz-change-email]');
      if (changeBtn && root.contains(changeBtn)) {
        e.preventDefault();
        forceEmailForm = true;
        var changeForm = root.querySelector('[data-quiz-save-form]');
        if (changeForm) {
          var cName = changeForm.querySelector('[data-quiz-save-name]');
          var cEmail = changeForm.querySelector('[data-quiz-save-email]');
          var cConsent = changeForm.querySelector('[data-quiz-save-consent]');
          if (cName) cName.value = answers.name || '';
          if (cEmail) cEmail.value = answers.email || '';
          if (cConsent) cConsent.checked = !!answers.consent;
        }
        showSavePanel('');
        updateSaveUi();
        var focusEmail = root.querySelector('[data-quiz-save-email]');
        if (focusEmail) focusEmail.focus();
        return;
      }

      var copyBtn = e.target.closest('[data-quiz-copy-btn]');
      if (copyBtn && root.contains(copyBtn)) {
        e.preventDefault();
        copyRitualLink(copyBtn);
        return;
      }

      var pdfBtn = e.target.closest('[data-quiz-pdf-btn]');
      if (pdfBtn && root.contains(pdfBtn)) {
        e.preventDefault();
        saveRitualPdf(pdfBtn);
        return;
      }

      var retakeBtn = e.target.closest('[data-quiz-retake]');
      if (retakeBtn && root.contains(retakeBtn)) {
        e.preventDefault();
        retakeQuiz();
        return;
      }
    });

    // Enter inside the guest save form submits it (the inputs live in a div,
    // not a <form>, so the browser gives them no default submit).
    root.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      var form = e.target.closest('[data-quiz-save-form]');
      if (!form || !root.contains(form)) return;
      e.preventDefault();
      var btn = form.querySelector('[data-quiz-save-submit]');
      if (btn && !btn.hasAttribute('disabled')) submitSaveForm(btn);
    });

    /* ── Save actions (email / copy link / PDF) ── */
    /* One shared confirmation line under the save buttons; each action sets
     * its own localized message (empty string hides it). */
    function showSavePanel(text) {
      var panel = root.querySelector('[data-quiz-save-panel]');
      if (!panel) return;
      panel.textContent = text || '';
      panel.classList.toggle('is-open', !!text);
    }

    /* Swap the save block between its branches. One-click send needs a known
     * email AND marketing consent (the Flow email goes out via "Send
     * marketing email", which only reaches subscribed customers); anything
     * less gets the inline form, which requires the consent checkbox. After a
     * successful send only the confirmation line stays. Everything needs the
     * relay tag configured; without it only link + PDF remain. */
    var forceEmailForm = false;
    var ritualEmailSent = false;
    function updateSaveUi() {
      var enabled = !!ritualEmailTag;
      var known = !!answers.email && !!answers.consent && !forceEmailForm;
      var showKnown = enabled && known && !ritualEmailSent;
      var showForm = enabled && !known && !ritualEmailSent;
      var intro = root.querySelector('[data-quiz-save-intro]');
      var sendBtn = root.querySelector('[data-quiz-email-btn]');
      var toLine = root.querySelector('[data-quiz-save-to]');
      var form = root.querySelector('[data-quiz-save-form]');
      if (intro) intro.hidden = !showKnown;
      if (sendBtn) sendBtn.hidden = !showKnown;
      if (toLine) {
        toLine.hidden = !showKnown;
        var toText = toLine.querySelector('[data-quiz-save-to-text]');
        if (toText) {
          var tpl = decodeEntities(toLine.getAttribute('data-template') || '');
          toText.textContent = tpl.replace('[[email]]', answers.email || '');
        }
      }
      if (form) {
        form.hidden = !showForm;
        if (showForm) {
          // Prefill with what we already know (gate contact, "Change" click).
          var nameEl = form.querySelector('[data-quiz-save-name]');
          var emailEl = form.querySelector('[data-quiz-save-email]');
          if (nameEl && !nameEl.value && answers.name) nameEl.value = answers.name;
          if (emailEl && !emailEl.value && answers.email) emailEl.value = answers.email;
        }
      }
    }

    /* Guest save (shared link, no stored contact): capture name + email
     * inline, then follow the same path as the gate - subscribe, persist,
     * personalize the result and send the ritual via Flow. */
    function submitSaveForm(btn) {
      var form = root.querySelector('[data-quiz-save-form]');
      if (!form) return;
      var nameEl = form.querySelector('[data-quiz-save-name]');
      var emailEl = form.querySelector('[data-quiz-save-email]');
      var consentEl = form.querySelector('[data-quiz-save-consent]');
      var errEl = form.querySelector('[data-quiz-save-error]');
      var name = nameEl ? nameEl.value.trim() : '';
      var email = emailEl ? emailEl.value.trim() : '';
      var valid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
      // Consent is required here: the Flow email goes out through "Send
      // marketing email", which never reaches unsubscribed customers.
      var consentOk = !consentEl || consentEl.checked;
      if (!name || !valid || !consentOk) {
        if (errEl) {
          errEl.textContent = (name && valid)
            ? decodeEntities(form.getAttribute('data-consent-error') || '')
            : decodeEntities(form.getAttribute('data-fields-error') || '');
          errEl.hidden = false;
        }
        if (!name && nameEl) nameEl.focus();
        else if (!valid && emailEl) emailEl.focus();
        else if (consentEl) consentEl.focus();
        return;
      }
      if (errEl) errEl.hidden = true;

      answers.name = name;
      answers.email = email;
      answers.consent = true;
      writeStoredContact({ name: name, email: email, consent: true });
      track('quiz_email_submitted', { marketing_consent: true, source: 'shared_result' });

      btn.setAttribute('disabled', 'disabled');
      requestRitualEmail().then(function () {
        track('quiz_ritual_saved', { method: 'email' });
        btn.removeAttribute('disabled');
        forceEmailForm = false;
        ritualEmailSent = true;
        // Re-render to personalize the headline/print head with the new name;
        // updateSaveUi inside leaves only the confirmation visible.
        renderResult();
        showSavePanel(decodeEntities(btn.getAttribute('data-sent-label') || ''));
      }, function () {
        btn.removeAttribute('disabled');
        showSavePanel(decodeEntities(btn.getAttribute('data-error-label') || ''));
      });
    }

    /* Primary save: POST the ritual to the Flow webhook so a workflow emails
     * the result link. Stays disabled after a successful send (one email is
     * enough); a retake re-enables it. */
    function sendRitualEmail(btn) {
      if (btn.hasAttribute('disabled')) return;
      btn.setAttribute('disabled', 'disabled');
      requestRitualEmail().then(function () {
        track('quiz_ritual_saved', { method: 'email' });
        btn.removeAttribute('disabled');
        // Hide the whole send branch: a lingering disabled button keeps its
        // cursor: progress and reads as stuck loading.
        ritualEmailSent = true;
        updateSaveUi();
        showSavePanel(decodeEntities(btn.getAttribute('data-sent-label') || ''));
      }, function () {
        btn.removeAttribute('disabled');
        showSavePanel(decodeEntities(btn.getAttribute('data-error-label') || ''));
      });
    }

    /* Secondary save: copy the shareable ?ritual= URL to the clipboard. */
    function copyRitualLink(btn) {
      var href = window.location.href;
      var done = function () {
        track('quiz_ritual_saved', { method: 'link' });
        showSavePanel(decodeEntities(btn.getAttribute('data-copied-label') || ''));
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(href).then(done, function () {
          legacyCopy(href);
          done();
        });
      } else {
        legacyCopy(href);
        done();
      }
    }

    function legacyCopy(text) {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (e) {}
      document.body.removeChild(ta);
    }

    /* Secondary save: print a standalone, self-contained ritual sheet from a
     * hidden iframe (letterhead + steps + products + link). Printing the shop
     * page itself is unreliable: site chrome, drawers and app embeds leak in
     * and theme CSS may not apply in the print context. */
    function buildPrintSheetHtml() {
      var ritualName = ritualNames[answers.q3] || defaultRitualName;
      var preparedFor = answers.name
        ? printRitualWithName.replace('{{ name }}', answers.name)
        : printRitualDefault;
      var container = root.querySelector('[data-quiz-result-steps]');
      var stepsHtml = '';
      var stepNum = 0;
      var seenVariants = {};

      STEP_ORDER.forEach(function (step) {
        var keys = ((lastRitual && lastRitual[step]) || []).filter(function (k) {
          var p = library[k];
          if (!p) return false;
          if (p.variantId) {
            if (seenVariants[p.variantId]) return false;
            seenVariants[p.variantId] = true;
          }
          return true;
        });
        if (!keys.length) return;
        stepNum++;
        var label = container ? (container.getAttribute('data-label-' + step) || step) : step;
        var cards = keys.map(function (k) {
          var p = library[k];
          return '<article class="card">' +
            (p.image ? '<img src="' + escapeHtml(p.image) + '" alt="">' : '<span class="card-ph"></span>') +
            '<h3>' + escapeHtml(p.title) + '</h3>' +
            (p.priceFormatted ? '<p class="price">' + escapeHtml(p.priceFormatted) + '</p>' : '') +
            (p.why ? '<p class="why">' + escapeHtml(p.why) + '</p>' : '') +
            '</article>';
        }).join('');
        stepsHtml += '<section class="step"><header><h2>' + escapeHtml(label) + '</h2>' +
          '<span>' + ('0' + stepNum).slice(-2) + '</span></header>' +
          '<div class="grid">' + cards + '</div></section>';
      });

      var offerEl = root.querySelector('.quiz-result__buyall-heading');
      var footer = (offerEl && !offerEl.hidden && offerEl.textContent.trim())
        ? '<footer>' + escapeHtml(offerEl.textContent.trim()) + '</footer>'
        : '';

      return '<!doctype html><html><head><meta charset="utf-8">' +
        '<title>' + escapeHtml(ritualName) + '</title>' +
        PRINT_SHEET_FONTS +
        '<style>' + PRINT_SHEET_CSS + '</style></head><body>' +
        '<header class="head">' +
        '<p class="brand">HADAT<span>Cosmetics</span></p>' +
        '<p class="for">' + escapeHtml(preparedFor) + '</p>' +
        '<h1>' + escapeHtml(ritualName) + '</h1>' +
        '<p class="url">' + escapeHtml(window.location.href) + '</p>' +
        '</header>' + stepsHtml + footer + '</body></html>';
    }

    function saveRitualPdf(btn) {
      if (!lastRitual) return;
      track('quiz_ritual_saved', { method: 'pdf' });
      showSavePanel(decodeEntities(btn.getAttribute('data-opening-label') || ''));

      var frame = document.createElement('iframe');
      frame.setAttribute('aria-hidden', 'true');
      frame.tabIndex = -1;
      // Keep it renderable (display:none breaks printing in some browsers),
      // just visually gone.
      frame.style.position = 'fixed';
      frame.style.right = '0';
      frame.style.bottom = '0';
      frame.style.width = '1px';
      frame.style.height = '1px';
      frame.style.border = '0';
      frame.style.visibility = 'hidden';
      document.body.appendChild(frame);

      var doc = frame.contentDocument;
      doc.open();
      doc.write(buildPrintSheetHtml());
      doc.close();

      var win = frame.contentWindow;
      var printed = false;
      var cleanup = function () {
        if (frame.parentNode) frame.parentNode.removeChild(frame);
      };
      var doPrint = function () {
        if (printed) return;
        printed = true;
        try {
          win.focus();
          win.print();
        } catch (e) {}
        // Safari may return from print() while the dialog is still open, so
        // don't tear the iframe down immediately.
        setTimeout(cleanup, 60000);
      };
      try { win.addEventListener('afterprint', function () { setTimeout(cleanup, 500); }); } catch (e) {}

      // Print once images (window load) and webfonts are in; hard fallback in
      // case either never settles.
      var whenLoaded = function () {
        var fontsReady = (doc.fonts && doc.fonts.ready) ? doc.fonts.ready : Promise.resolve();
        Promise.resolve(fontsReady).then(function () { setTimeout(doPrint, 150); }, doPrint);
      };
      if (doc.readyState === 'complete') whenLoaded();
      else win.addEventListener('load', whenLoaded);
      setTimeout(doPrint, 3500);
    }

    /* Start over from the hero: resets answers, the form, option selection
     * state and the shareable URL, then tracks quiz_retaken. */
    function retakeQuiz() {
      track('quiz_retaken');
      answers = {};
      lastRitual = null;
      started = false;

      screens.forEach(function (s) {
        s.querySelectorAll('[data-quiz-option]').forEach(function (o) {
          o.classList.remove('is-selected');
          o.setAttribute('aria-pressed', 'false');
        });
      });

      if (byName.email) {
        var nameEl = byName.email.querySelector('[data-quiz-name]');
        var emailEl = byName.email.querySelector('[data-quiz-email]');
        var consentEl = byName.email.querySelector('[data-quiz-consent]');
        var errEl = byName.email.querySelector('[data-quiz-email-error]');
        if (nameEl) nameEl.value = '';
        if (emailEl) emailEl.value = '';
        if (consentEl) consentEl.checked = false;
        if (errEl) errEl.hidden = true;
      }

      showSavePanel('');
      clearStoredContact();
      forceEmailForm = false;
      ritualEmailSent = false;
      resultViewed = false;
      var emailSaveBtn = root.querySelector('[data-quiz-email-btn]');
      if (emailSaveBtn) emailSaveBtn.removeAttribute('disabled');
      var saveForm = root.querySelector('[data-quiz-save-form]');
      if (saveForm) {
        saveForm.querySelectorAll('input[type="text"], input[type="email"]').forEach(function (el) {
          el.value = '';
        });
        var saveConsent = saveForm.querySelector('[data-quiz-save-consent]');
        if (saveConsent) saveConsent.checked = false;
        var saveErr = saveForm.querySelector('[data-quiz-save-error]');
        if (saveErr) saveErr.hidden = true;
        var saveSubmitBtn = saveForm.querySelector('[data-quiz-save-submit]');
        if (saveSubmitBtn) saveSubmitBtn.removeAttribute('disabled');
      }

      try {
        var url = new URL(window.location.href);
        url.searchParams.delete('ritual');
        url.searchParams.delete('name');
        stripTrackingParams(url);
        window.history.replaceState({}, '', url.toString());
      } catch (e) {}

      show('intro');
      window.scrollTo({ top: 0, behavior: reducedMotion ? 'auto' : 'smooth' });
    }

    /* ── Email gate ── */
    function submitEmail() {
      var nameEl = byName.email.querySelector('[data-quiz-name]');
      var emailEl = byName.email.querySelector('[data-quiz-email]');
      var consentEl = byName.email.querySelector('[data-quiz-consent]');
      var errEl = byName.email.querySelector('[data-quiz-email-error]');
      var name = nameEl ? nameEl.value.trim() : '';
      var email = emailEl ? emailEl.value.trim() : '';
      var valid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);

      if (!name || !valid) {
        if (errEl) errEl.hidden = false;
        if (!name && nameEl) nameEl.focus();
        else if (emailEl) emailEl.focus();
        return;
      }
      if (errEl) errEl.hidden = true;

      answers.name = name;
      answers.email = email;
      answers.consent = consentEl ? consentEl.checked : false;
      writeStoredContact({ name: name, email: email, consent: answers.consent });

      track('quiz_email_submitted', { marketing_consent: answers.consent });
      subscribeCustomer(name, email, answers.consent).catch(function () {});
      renderResult();
      show('result', 'forward');
      markResultViewed();
      writeShareUrl();
      window.scrollTo({ top: 0, behavior: reducedMotion ? 'auto' : 'smooth' });
    }

    /* Create/update the customer through the SERVER-RENDERED {% form
     * 'customer' %} relay in quiz.liquid. It must be that Liquid form:
     * Shopify's storefront bot protection 403s synthetic requests (verified
     * live: bare POSTs to /contact get a "Verifying your connection"
     * challenge page).
     *
     * Two transports:
     * - opts.returnTo set: FULL-PAGE navigation. Used for the ritual email
     *   request, where delivery must be certain: a bot challenge renders
     *   natively full-screen (the hidden-iframe variant is a dead end - the
     *   challenge document is origin-isolated, so it can neither be detected
     *   nor completed there), the customer is written, and Shopify redirects
     *   back to returnTo (?quiz_sent=1 restores the result + confirmation).
     * - otherwise: best-effort submit into the hidden iframe (gate path);
     *   resolves optimistically, a challenged submission may be dropped. */
    function subscribeCustomer(name, email, consent, extraTags, opts) {
      opts = opts || {};
      var form = root.querySelector('[data-quiz-relay-form]');
      var frame = root.querySelector('[data-quiz-subscribe-frame]');
      if (!form || !frame) {
        return Promise.reject(new Error('quiz: contact relay form missing'));
      }
      var emailInput = form.querySelector('[data-quiz-relay-email]');
      var nameInput = form.querySelector('[data-quiz-relay-name]');
      var tagsInput = form.querySelector('[data-quiz-relay-tags]');
      var returnInput = form.querySelector('[data-quiz-relay-return]');
      if (emailInput) emailInput.value = email;
      if (nameInput) nameInput.value = name;
      if (tagsInput) {
        tagsInput.value = [consent ? 'quiz, newsletter' : 'quiz'].concat(extraTags || []).join(', ');
      }

      if (opts.returnTo) {
        if (returnInput) returnInput.value = opts.returnTo;
        form.removeAttribute('target');
        form.submit();
        // The page is navigating away; hand back a promise that never
        // settles so callers don't flash any post-submit state.
        return new Promise(function () {});
      }

      if (returnInput) returnInput.value = '';
      form.setAttribute('target', frame.getAttribute('name'));
      return new Promise(function (resolve) {
        var settled = false;
        var settle = function () {
          if (settled) return;
          settled = true;
          frame.removeEventListener('load', settle);
          resolve();
        };
        frame.addEventListener('load', settle);
        setTimeout(settle, 8000);
        form.submit();
      });
    }

    /* Ask Flow to email the ritual link. Shopify Flow has no incoming-webhook
     * trigger (and Flow app extensions in custom apps are Plus-only), so the
     * relay is two customer tags set through the same /contact form:
     *   - ritualEmailTag (fixed, from section settings): the Flow workflow
     *     triggers on "Customer tags added" filtered to this tag,
     *   - "quiz-ritual-<encoded answers>": carries the data; the workflow's
     *     "Send internal email" action rebuilds ?ritual=<encoded> from it.
     * The workflow then removes both tags, which re-arms the trigger for the
     * next request from the same customer. */
    function requestRitualEmail() {
      if (!ritualEmailTag || !lastRitual || !answers.email) {
        return Promise.reject(new Error('quiz: ritual email not configured or no result yet'));
      }
      // Come back to this exact result with the sent-state flag; restore
      // rebuilds the ritual from ?ritual= and the contact from storage.
      var returnTo = window.location.pathname + '?quiz_sent=1';
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('ritual', encodeAnswers(answers));
        url.searchParams.delete('name');
        stripTrackingParams(url);
        url.searchParams.set('quiz_sent', '1');
        returnTo = url.pathname + url.search;
      } catch (e) {}
      return subscribeCustomer(
        answers.name || '',
        answers.email,
        answers.consent,
        [ritualEmailTag, 'quiz-ritual-' + encodeAnswers(answers)],
        { returnTo: returnTo }
      );
    }

    /* Fire quiz_result_viewed once per completed run (reset on retake). */
    var resultViewed = false;
    function markResultViewed() {
      if (resultViewed) return;
      resultViewed = true;
      track('quiz_result_viewed', resultMeta());
    }

    /* ── Result rendering ── */
    var lastRitual = null;
    function resultMeta() {
      var ids = [];
      STEP_ORDER.forEach(function (step) {
        (lastRitual ? lastRitual[step] : []).forEach(function (k) {
          if (library[k] && library[k].variantId && ids.indexOf(library[k].variantId) === -1) {
            ids.push(library[k].variantId);
          }
        });
      });
      return { ritual_id: encodeAnswers(answers), product_ids: ids };
    }

    /* Build one product card from the shared template. Used for the ritual
     * strip and the upsell strip alike; ritual cards carry their step label
     * as a small tag, sold-out cards render with a badge and an inert "Sold
     * out" button, unpurchasable ones lose the button. */
    function buildCard(p, stepLabel) {
      var cardTpl = root.querySelector('[data-quiz-card-template]');
      if (!cardTpl || !p) return null;

      // A product is purchasable only if it maps to a real, in-stock variant.
      // Sold-out (available === false) is shown but never added to the cart —
      // Shopify rejects the WHOLE bundle add if any single line is sold out.
      var soldOut = !!p.variantId && p.available === false;
      var purchasable = !!p.variantId && !soldOut;

      var card = cardTpl.content.firstElementChild.cloneNode(true);
      var img = card.querySelector('[data-card-img]');
      var link = card.querySelector('[data-card-link]');
      var titleLink = card.querySelector('[data-card-title-link]');
      var priceEl = card.querySelector('[data-card-price]');
      var whyEl = card.querySelector('[data-card-why]');
      var addBtn = card.querySelector('[data-card-add]');
      var soldOutBadge = card.querySelector('[data-card-soldout]');
      var stepEl = card.querySelector('[data-card-step]');

      if (stepEl) {
        if (stepLabel) { stepEl.textContent = stepLabel; stepEl.hidden = false; }
        else { stepEl.remove(); }
      }
      if (img) {
        if (p.image) { img.src = p.image; img.alt = p.title; }
        else { img.remove(); }
      }
      if (link) link.href = p.url || '#';
      if (titleLink) { titleLink.textContent = p.title; titleLink.href = p.url || '#'; }
      if (priceEl) priceEl.textContent = p.priceFormatted || formatMoney(p.price, moneyFormat);
      if (whyEl) {
        if (p.why) { whyEl.textContent = p.why; whyEl.hidden = false; }
        else { whyEl.remove(); }
      }

      if (soldOut) {
        card.classList.add('quiz-card--soldout');
        if (soldOutBadge) soldOutBadge.hidden = false;
        if (addBtn) {
          // Keep the button visible but inert: drop the hook the click
          // handler keys off, disable it, and relabel it "Sold out".
          addBtn.removeAttribute('data-card-add');
          addBtn.setAttribute('disabled', 'disabled');
          var soLabel = addBtn.querySelector('span');
          if (soLabel) soLabel.textContent = soldOutLabel;
        }
      } else if (addBtn) {
        if (purchasable) addBtn.setAttribute('data-variant-id', p.variantId);
        else addBtn.remove();
      }

      return { card: card, purchasable: purchasable, soldOut: soldOut };
    }

    /* Compose the order-of-use narrative under the result headline. Sentence
     * frames arrive Liquid-translated in data attributes with [[product]] /
     * [[products]] tokens; each product's why-line is appended as its own
     * full sentence (the why copy is standalone prose - it cannot be embedded
     * mid-sentence). Body/hands close the paragraph as one list sentence. */
    function composeNarrative(hairProducts, bodyProducts) {
      var el = root.querySelector('[data-quiz-narrative]');
      if (!el) return;
      var tpl = function (attr) { return decodeEntities(el.getAttribute(attr) || ''); };
      var sentences = hairProducts.map(function (p, i) {
        var frame = i === 0 ? tpl('data-tpl-start')
          : i === hairProducts.length - 1 ? tpl('data-tpl-finish')
          : tpl('data-tpl-follow');
        var s = frame.replace('[[product]]', p.title);
        return p.why ? s + ' ' + p.why : s;
      });
      if (bodyProducts.length) {
        var names = bodyProducts.map(function (p) { return p.title; });
        var join = tpl('data-join-word') || 'and';
        var list = names.length === 1 ? names[0]
          : names.slice(0, -1).join(', ') + ' ' + join + ' ' + names[names.length - 1];
        sentences.push(tpl('data-tpl-body').replace('[[products]]', list));
      }
      el.textContent = sentences.join(' ');
      el.hidden = sentences.length === 0;
    }

    /* Pick up to `count` upsell products outside the ritual, deterministically:
     * body/hands first when the ritual has none, then a mask/oil when the
     * ritual carries no indulgence step, then the weekly scrub, then the fixed
     * fill list. Only purchasable products qualify. */
    function pickUpsells(ritual, count) {
      var inRitual = {};
      var usedVariants = {};
      STEP_ORDER.forEach(function (step) {
        (ritual[step] || []).forEach(function (k) {
          inRitual[k] = true;
          if (library[k] && library[k].variantId) usedVariants[library[k].variantId] = true;
        });
      });
      var hasBody = (ritual.body_hands || []).length > 0;
      var hasMaskOrOil = MASK_OIL_KEYS.some(function (k) { return inRitual[k]; });

      var candidates = [];
      if (!hasBody) candidates.push('calm-shower-gel', 'aya-hand-cream');
      if (!hasMaskOrOil) candidates.push('spa-treatment', 'silk-oil');
      candidates.push('mud-scrub');
      candidates = candidates.concat(UPSELL_FILL);

      var picks = [];
      candidates.forEach(function (k) {
        if (picks.length >= count) return;
        var p = library[k];
        if (!p || inRitual[k] || !p.variantId || p.available === false) return;
        if (usedVariants[p.variantId]) return;
        inRitual[k] = true;
        usedVariants[p.variantId] = true;
        picks.push(p);
      });
      return picks;
    }

    function renderResult() {
      lastRitual = buildRitual(answers);

      // Hero: name + ritual name
      var ritualName = ritualNames[answers.q3] || defaultRitualName;

      // Give shared links / bookmarks a meaningful tab title (aligns with the
      // ESP email subject: the user's ritual name).
      try {
        document.title = (answers.name ? answers.name + ' · ' : '') + ritualName + ' · HADAT';
      } catch (e) {}
      root.querySelectorAll('[data-quiz-name-out]').forEach(function (el) {
        el.textContent = answers.name || '';
      });
      // The headline copy starts with the separator after the name (", here
      // is your..."); with no name to greet, drop that leading punctuation.
      root.querySelectorAll('[data-quiz-headline-rest]').forEach(function (el) {
        var full = decodeEntities(el.getAttribute('data-full') || '') || el.textContent;
        el.textContent = answers.name ? full : full.replace(/^[\s,.;:!]+/, '');
      });
      root.querySelectorAll('[data-quiz-ritual-name]').forEach(function (el) {
        el.textContent = ritualName;
      });

      // Print-only header (Save my ritual → PDF).
      root.querySelectorAll('[data-quiz-print-name]').forEach(function (el) {
        el.textContent = answers.name
          ? printRitualWithName.replace('{{ name }}', answers.name)
          : printRitualDefault;
      });
      root.querySelectorAll('[data-quiz-print-ritual]').forEach(function (el) {
        el.textContent = ritualName;
      });

      // One flat line of cards in order of use, each tagged with its step label.
      var container = root.querySelector('[data-quiz-result-steps]');
      if (!container) return;
      container.innerHTML = '';

      var totalCents = 0;
      var variantIds = [];
      var shownProducts = [];
      var hairProducts = [];
      var bodyProducts = [];
      var soldOutCount = 0;
      // Guard against the same Shopify product surfacing twice across steps
      // (e.g. distinct spec keys mapped to one catalogue product). De-dupe by
      // variantId so a product is never shown — or added to cart — twice.
      var seenVariants = {};

      STEP_ORDER.forEach(function (step) {
        var keys = (lastRitual[step] || []).filter(function (k) {
          if (!library[k]) return false;
          var vid = library[k].variantId;
          if (vid) {
            if (seenVariants[vid]) return false;
            seenVariants[vid] = true;
          }
          return true;
        });
        var stepLabel = container.getAttribute('data-label-' + step) || step;

        keys.forEach(function (key) {
          var built = buildCard(library[key], stepLabel);
          if (!built) return;
          var p = library[key];
          if (built.soldOut) soldOutCount++;
          container.appendChild(built.card);
          shownProducts.push(p);
          (step === 'body_hands' ? bodyProducts : hairProducts).push(p);

          // Only in-stock items count toward the bundle add + its total, so the
          // "Add the full ritual" button always succeeds with what's available.
          if (built.purchasable) {
            if (typeof p.price === 'number') totalCents += p.price;
            variantIds.push(p.variantId);
          }
        });
      });

      composeNarrative(hairProducts, bodyProducts);

      // "Add to your bundle": up to 3 purchasable library products outside the
      // ritual. They reuse the card template + per-card add handler and are
      // NOT counted into the bundle total.
      var upsellEl = root.querySelector('[data-quiz-upsell]');
      var upsellGrid = root.querySelector('[data-quiz-upsell-grid]');
      if (upsellEl && upsellGrid) {
        upsellGrid.innerHTML = '';
        var upsellPicks = pickUpsells(lastRitual, 3);
        upsellPicks.forEach(function (p) {
          var built = buildCard(p);
          if (built) upsellGrid.appendChild(built.card);
        });
        upsellEl.hidden = upsellPicks.length === 0;
      }

      // Buy-all total. The bundle discount code requires at least 3 eligible
      // items in the cart, so below 3 purchasable products the 20% promise
      // would not survive checkout: hide every offer line ([data-quiz-offer])
      // and price as plain total instead.
      var bundleEligible = discountPercent > 0 && variantIds.length >= 3;
      var discounted = bundleEligible ? Math.round(totalCents * (1 - discountPercent / 100)) : totalCents;
      var buyAllBtn = root.querySelector('[data-quiz-buyall-btn]');
      if (buyAllBtn) {
        buyAllBtn.setAttribute('data-variant-ids', variantIds.join(','));
        buyAllBtn.setAttribute('data-bundle-eligible', bundleEligible ? 'true' : 'false');
      }
      // "This personalized ritual is X% off when you order all N products." -
      // N counts the purchasable ritual items (matches the bundle add).
      root.querySelectorAll('[data-quiz-discount-line]').forEach(function (el) {
        var lineTpl = decodeEntities(el.getAttribute('data-template') || '');
        el.textContent = lineTpl.replace('[[count]]', String(variantIds.length));
      });
      root.querySelectorAll('[data-quiz-offer]').forEach(function (el) {
        el.hidden = !bundleEligible;
      });
      // When sold-out items are what pushed the ritual under the 3-item
      // minimum, say so instead of silently dropping the offer.
      var soldNote = root.querySelector('[data-quiz-soldout-note]');
      if (soldNote) soldNote.hidden = bundleEligible || soldOutCount === 0;
      root.querySelectorAll('[data-quiz-buyall-total]').forEach(function (el) {
        el.textContent = formatMoney(discounted, moneyFormat);
      });
      root.querySelectorAll('[data-quiz-buyall-was]').forEach(function (el) {
        el.textContent = formatMoney(totalCents, moneyFormat);
        el.hidden = !bundleEligible;
      });

      updateSaveUi();
    }

    /* ── Cart ── */
    function addFullRitual(btn) {
      var ids = (btn.getAttribute('data-variant-ids') || '').split(',').filter(Boolean);
      var label = btn.querySelector('[data-quiz-buyall-label]') || btn;
      var original = label.textContent;
      // No purchasable variants means the ritual products aren't wired to real,
      // available Shopify products yet (unassigned, unpublished to the Online
      // Store channel, or out of stock). Surface it instead of dying silently —
      // a dead button reads as "broken", a message points at the real cause.
      if (!ids.length) {
        if (window.console) {
          console.warn('[quiz] "Add the full ritual" has no purchasable variants — ' +
            'check that every ritual product is assigned and published to the Online Store.');
        }
        label.textContent = cartError;
        setTimeout(function () { label.textContent = original; }, 3000);
        return;
      }
      btn.setAttribute('disabled', 'disabled');
      label.textContent = '...';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: ids.map(function (id) { return { id: Number(id), quantity: 1 }; }) })
      })
        .then(function (r) { return r.ok ? r.json() : r.json().then(function (e) { return Promise.reject(e); }); })
        .then(function () {
          track('quiz_add_full_ritual_to_cart', { product_ids: ids });
          label.textContent = original;
          btn.removeAttribute('disabled');
          // Apply the bundle discount code (sets the discount cookie) then open the cart.
          // Only dispatch cart:open (not cart:updated first) to avoid a double-refresh race.
          var openCart = function () {
            document.dispatchEvent(new CustomEvent('cart:open'));
          };
          // Only pre-apply the code when the ritual meets its 3-item minimum;
          // otherwise checkout would drop it and the cart would look broken.
          if (discountCode && btn.getAttribute('data-bundle-eligible') === 'true') {
            fetch('/discount/' + encodeURIComponent(discountCode), { method: 'GET' })
              .then(openCart, openCart);
          } else {
            openCart();
          }
        })
        .catch(function (err) {
          var msg = err && err.description ? err.description : cartError;
          label.textContent = msg;
          setTimeout(function () {
            label.textContent = original;
            btn.removeAttribute('disabled');
          }, 3000);
        });
    }

    // Per-card add-to-cart (ritual + upsell cards share the template/handler).
    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-card-add]');
      if (!btn || !root.contains(btn)) return;
      var id = btn.getAttribute('data-variant-id');
      if (!id) return;
      var isUpsell = !!btn.closest('[data-quiz-upsell]');
      btn.disabled = true;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: Number(id), quantity: 1 })
      })
        .then(function (r) { return r.ok ? r.json() : Promise.reject(r); })
        .then(function () {
          track(isUpsell ? 'quiz_upsell_product_added' : 'quiz_individual_product_added', { product_id: id });
          document.dispatchEvent(new CustomEvent('cart:updated'));
          document.dispatchEvent(new CustomEvent('cart:open'));
        })
        .catch(function () {})
        .finally(function () { btn.disabled = false; });
    });

    /* ── Shareable URL (?ritual=...) ── */
    function writeShareUrl() {
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('ritual', encodeAnswers(answers));
        // No personal data in the shareable URL: the contact lives in this
        // browser's storage, and the link is meant to be passed around.
        url.searchParams.delete('name');
        url.searchParams.delete('quiz_sent');
        stripTrackingParams(url);
        window.history.replaceState({}, '', url.toString());
      } catch (e) {}
      updatePrintUrl();
    }

    /* Mirror the current shareable URL into the print-only header. */
    function updatePrintUrl() {
      var href = window.location.href;
      root.querySelectorAll('[data-quiz-print-url]').forEach(function (el) {
        el.textContent = href;
      });
    }

    /* ── Sticky buyall bar (mobile/tablet only) ── */
    var stickyBar  = root.querySelector('[data-quiz-sticky]');
    var stickyAside = root.querySelector('.quiz-result__aside');
    if (stickyBar && stickyAside && 'IntersectionObserver' in window) {
      var stickyObserver = new IntersectionObserver(function (entries) {
        var resultActive = byName.result && byName.result.classList.contains('is-active');
        var asideVisible = entries[0].isIntersecting;
        var shouldShow   = resultActive && !asideVisible;
        stickyBar.classList.toggle('is-visible', shouldShow);
        stickyBar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      }, { threshold: 0.1 });
      stickyObserver.observe(stickyAside);

      stickyBar.addEventListener('click', function (e) {
        if (!e.target.closest('[data-quiz-sticky-btn]')) return;
        stickyAside.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    /* ── Restore a shared result on load ── */
    function restoreFromUrl() {
      var params;
      try { params = new URL(window.location.href).searchParams; } catch (e) { return false; }
      var ritual = params.get('ritual');
      if (!ritual) return false;
      var restored = decodeAnswers(ritual);
      if (!restored.q3) return false; // need at least the primary goal
      answers = restored;
      // Contact comes from this browser's storage, never from the URL (a
      // legacy ?name= param is ignored on purpose).
      var stored = readStoredContact();
      answers.name = stored.name || '';
      answers.email = stored.email || '';
      answers.consent = !!stored.consent;
      renderResult();
      show('result');
      markResultViewed();
      // Normalize the address bar right away: email click-throughs arrive
      // with tracking params that shouldn't survive into a copied link.
      writeShareUrl();

      // Back from the full-page contact submission (?quiz_sent=1): show the
      // sent confirmation and bring the save block into view.
      if (params.get('quiz_sent') === '1') {
        ritualEmailSent = true;
        updateSaveUi();
        var sentBtn = root.querySelector('[data-quiz-email-btn]');
        showSavePanel(decodeEntities((sentBtn && sentBtn.getAttribute('data-sent-label')) || ''));
        var aside = root.querySelector('.quiz-result__aside');
        if (aside) {
          setTimeout(function () {
            aside.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
          }, 150);
        }
      }
      return true;
    }

    /* ── Boot ── */
    if (!restoreFromUrl()) {
      if (byName.intro) show('intro');
      else if (questionScreens[0]) show(questionScreens[0].getAttribute('data-screen'));
    }
  }

  function boot() {
    document.querySelectorAll('[data-quiz]').forEach(initQuiz);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
